import { MediaDeviceRepository, MediaDeviceStatusListener, MediaDeviceStatusErrorListener, MediaDeviceStatusError } from 'core/repositories/MediaDeviceRepository';
import { MediaDevice } from 'core/models/mediaDevice';
import { StreamConnector } from './api/StreamConnector';
import { Lazy, lazy } from 'utility/Lazy';
import { createKeyedListenerManager } from 'utility/KeyedListenerManager';
import { ApiCall } from './api/apiCall';
import { StableWebSocket } from 'infrastructure/utility/StableWebSocket';
import { createListenerManager } from 'infrastructure/utility/ListenerManager';
import { getMediaDeviceConfig, patchMediaDeviceConfig } from './api/MediaDevice';
import { Project } from 'core/models/project';
import { Environment } from 'core/models/environment';

type MediaStatus = {
    type: 'MediaStatus';
    body: {
        device_id: MediaDevice.Id;
        status: {
            started?: boolean;
        };
    };
}

type KeepAlive = {
    type: 'KeepAlive';
    body: null;
}

function equalStatus(lhs: MediaDevice.Status, rhs: MediaDevice.Status): boolean {
    if (lhs.started != rhs.started) return false;
    if (lhs.resolution) {
        if (!rhs.resolution) {
            return false;
        } else {
            if (lhs.resolution != rhs.resolution) {
                return false;
            }
        }
    } else {
        if (rhs.resolution) {
            return false;
        }
    }
    return true;
}

class ApiMediaDeviceRepository implements MediaDeviceRepository {
    private apiCall: ApiCall;
    private connection: Lazy<StableWebSocket>;

    private projectId: Project.Id | undefined;

    private readonly deviceToProject = new Map<MediaDevice.Id, Project.Id>();

    private readonly listenerManager = createKeyedListenerManager<MediaDevice.Id, MediaDeviceStatusListener>(key => {
        this.updateSubscription();

        return () => {
            this.lastStatusMap.delete(key);
            this.updateSubscription();
        };
    });

    constructor(apiCall: ApiCall, connectStream: StreamConnector) {
        this.apiCall = apiCall;
        // FIXME: 現状は起動時に毎回繋ぎに行くが、必要な時だけコネクションを張れるようになるとベスト
        // WebSocketのラッパーを作るのが素直そう
        this.connection = lazy(this.setupConnection(connectStream));
    }

    addStatusListener(mediaDeviceId: MediaDevice.Id, projectId: Project.Id, listener: MediaDeviceStatusListener): void {
        if (!this.projectId) {
            this.projectId = projectId;
        }
        this.deviceToProject.set(mediaDeviceId, projectId);
        this.listenerManager.addListener(mediaDeviceId, listener);

        const lastStatus = this.lastStatusMap.get(mediaDeviceId);
        if (lastStatus !== undefined) {
            listener(lastStatus);
        }
    }

    removeStatusListener(mediaDeviceId: MediaDevice.Id, _projectId: Project.Id, listener: MediaDeviceStatusListener): void {
        this.listenerManager.removeListener(mediaDeviceId, listener);
        this.deviceToProject.delete(mediaDeviceId);
    }

    private readonly errorListenerManager = createListenerManager<MediaDeviceStatusErrorListener>();

    addMediaDeviceStatusErrorListener(listener: MediaDeviceStatusErrorListener) {
        this.errorListenerManager.addListener(listener);
    }

    removeMediaDeviceStatusErrorListener(listener: MediaDeviceStatusErrorListener) {
        this.errorListenerManager.removeListener(listener);
    }

    async startPublish(mediaDeviceId: MediaDevice.Id, projectId: Project.Id, environmentId: Environment.Id) {
        await this.apiCall<{}>(`/v1/projects/${projectId}/environments/${environmentId}/devices/${mediaDeviceId}/status`, {
            method: 'PUT',
            body: JSON.stringify({ started: true }),
            mode: 'cors',
            headers: { 'Content-Type': 'application/json' }
        });
    }

    async stopPublish(mediaDeviceId: MediaDevice.Id, projectId: Project.Id, environmentId: Environment.Id) {
        await this.apiCall<{}>(`/v1/projects/${projectId}/environments/${environmentId}/devices/${mediaDeviceId}/status`, {
            method: 'PUT',
            body: JSON.stringify({ started: false }),
            mode: 'cors',
            headers: { 'Content-Type': 'application/json' }
        });
    }

    async restartPublish(mediaDeviceId: MediaDevice.Id, projectId: Project.Id, environmentId: Environment.Id) {
        await this.apiCall<{}>(`/v1/projects/${projectId}/environments/${environmentId}/devices/${mediaDeviceId}/restart`, {
            method: 'POST',
        });
    }

    async getVideoParams(mediaDeviceId: MediaDevice.Id, projectId: Project.Id, environmentId: Environment.Id) {
        const res = await getMediaDeviceConfig(this.apiCall, mediaDeviceId, projectId, environmentId);
        const {
            video_bitrate: rawMaxVideoBitrate,
            min_video_bitrate: rawMinVideoBitrate,
            video_codec: videoCodec,
            video_framerate: framerate,
            video_resolution: videoResolution,
        } = res.config;

        const maxBitrate = rawMaxVideoBitrate !== undefined ? rawMaxVideoBitrate * 1000 : undefined;
        const minBitrate = rawMinVideoBitrate !== undefined ? rawMinVideoBitrate * 1000 : undefined;

        const codec = videoCodec && MediaDevice.isKnownVideoCodec(videoCodec) ? videoCodec : undefined;
        const unknownCodec = videoCodec && !MediaDevice.isKnownVideoCodec(videoCodec) ? videoCodec : undefined;

        const resolution = videoResolution && MediaDevice.isPredefinedResolution(videoResolution) ? videoResolution : undefined;

        const config: Partial<MediaDevice.VideoConfig> = {
            maxBitrate,
            minBitrate,
            codec,
            unknownCodec,
            framerate,
            resolution,
        };
        return config;
    }

    async setVideoParams(mediaDeviceId: MediaDevice.Id, projectId: Project.Id, environmentId: Environment.Id, videoParams: Partial<MediaDevice.VideoConfig>) {
        if (Object.values(videoParams).filter(v => v != undefined).length === 0) return;

        const {
            enabled = true,
            maxBitrate,
            minBitrate,
            framerate,
            resolution,
        } = videoParams;
        const codec = 'codec' in videoParams ? videoParams.codec : undefined;

        await patchMediaDeviceConfig(this.apiCall, mediaDeviceId, projectId, environmentId, {
            'no_video': !enabled,
            'video_codec': codec,
            'video_framerate': framerate,
            'video_bitrate': maxBitrate !== undefined ? Math.floor(maxBitrate / 1000) : undefined, // 1-30000 [kbps]
            'min_video_bitrate': minBitrate !== undefined ? Math.floor(minBitrate / 1000) : undefined,
            'video_resolution': resolution,
        });
    }

    async getAudioParams(mediaDeviceId: MediaDevice.Id, projectId: Project.Id, environmentId: Environment.Id) {
        const res = await getMediaDeviceConfig(this.apiCall, mediaDeviceId, projectId, environmentId);
        const {
            audio_bitrate: maxBitrate,
            audio_codec: audioCodec,
        } = res.config;

        const codec = audioCodec && MediaDevice.isKnownAudioCodec(audioCodec) ? audioCodec : undefined;
        const unknownCodec = audioCodec && !MediaDevice.isKnownAudioCodec(audioCodec) ? audioCodec : undefined;

        const config: Partial<MediaDevice.AudioConfig> = {
            maxBitrate,
            codec,
            unknownCodec,
        };
        return config;
    }

    async setAudioParams(mediaDeviceId: MediaDevice.Id, projectId: Project.Id, environmentId: Environment.Id, audioParams: Partial<MediaDevice.AudioConfig>) {
        if (Object.values(audioParams).filter(v => v != undefined).length === 0) return;

        const {
            enabled = true,
            maxBitrate,
        } = audioParams;
        const codec = 'codec' in audioParams ? audioParams.codec : undefined;

        await patchMediaDeviceConfig(this.apiCall, mediaDeviceId, projectId, environmentId, {
            'no_audio': !enabled,
            'audio_codec': codec,
            'audio_bitrate': maxBitrate ? Math.floor(maxBitrate / 1000) : undefined, // 6-510 [kbps]
        });
    }

    private readonly lastStatusMap = new Map<MediaDevice.Id, MediaDevice.Status | undefined>();

    private async setupConnection(connectStream: StreamConnector) {
        const ws = new StableWebSocket(() => connectStream('/v1/media/connect'));

        ws.onclose = () => {
            console.log('ws for MT status is closed');
        };
        ws.onerror = err => {
            console.error('ws for MT status got an error', err);
        };
        ws.onmessage = msg => {
            if (!msg.data) {
                console.warn(`no 'data' field in message: ${JSON.stringify(msg)}`);
                return;
            }

            // AWS IoT Shadow Status
            const data = JSON.parse(msg.data) as MediaStatus | KeepAlive;
            switch (data.type) {
                case 'MediaStatus':
                    {
                        const mediaDeviceId = data.body.device_id;
                        const listeners = this.listenerManager.getListeners(mediaDeviceId);
                        if (listeners === undefined || listeners.length == 0) {
                            break;
                        }

                        const started = data.body.status.started ?? false;
                        const status = { started };
                        const lastStatus = this.lastStatusMap.get(mediaDeviceId);
                        if (lastStatus && equalStatus(lastStatus, status)) {
                            break;
                        }

                        console.info(`MediaStatus: ${mediaDeviceId} started: ${started}`);
                        this.lastStatusMap.set(mediaDeviceId, status);
                        listeners.forEach(listener => listener(status));
                    }
                    break;
                case 'KeepAlive':
                    break;
                default:
                    console.warn(`unknown media device message: ${msg.data}`);
            }
        };

        return await ws.connect();
    }

    private async updateSubscription() {
        try {
            const ws = await this.connection.get();

            const devices = [...this.listenerManager.keys()];
            await ws.send(JSON.stringify({
                'type': 'SubscribeMediaStatusRequest',
                'body': {
                    'project_id': this.projectId,
                    'device_ids': devices,
                }
            }));
        } catch (e) {
            if (e instanceof MediaDeviceStatusError) {
                const mediaDeviceStatusError = e;
                [...this.errorListenerManager.listeners].forEach(listener => {
                    listener(mediaDeviceStatusError);
                });
            } else {
                throw e;
            }
        }
    }
}

export { ApiMediaDeviceRepository as MediaDeviceRepository };
